<template>
  <div
    v-if="accordions"
    class="accordion"
    :class="[
      $blockClass(blockClass, 'wrapper'),
    ]"
  >
    <div
      v-for="(accordion, index) of accordions"
      :key="index"
      class="accordion__item"
      :class="[
        $blockClass(blockClass, 'accordion'),
      ]"
    >
      <component
        :is="toggleHeadingTag"
      >
        <button
          type="button"
          class="accordion__trigger"
          :class="[
            $blockClass(blockClass, 'trigger'),
            { [$class('active')]: isActive(index) && !useMinusIcon }
          ]"
          :aria-expanded="isActive(index)"
          @click="toggleAccordion(index)"
        >
          <span
            class="accordion__trigger-label"
            :class="[
              $blockClass(blockClass, 'trigger-label'),
              { [$class('active')]: isActive(index) || expandedIndex === index }
            ]"
            v-text="getAccordionTitle(accordion)"
          />

          <icon-minus v-if="isActive(index) && useMinusIcon" />

          <new-plus v-else />
        </button>
      </component>

      <transition-height>
        <div
          v-show="isActive(index)"
          class="rte accordion__body"
          :class="[
            $blockClass(blockClass, 'body'),
          ]"
          v-html="formatBody(accordion.body)"
        />
      </transition-height>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Accordion Component (accordion-component)
 * -----------------------------------------------------------------------------
 * Global accordion component
 *
 * @param {Array} accordionData - Array of objects containing title and body.
 * @param {String} blockClass - Block level class to apply to all elements.
 * @param {String} toggleHeadingTag - Toggle heading tag.
 * @param {Boolean} [useMinusIcon] - Option to use minus icon.
 */
import TransitionHeight from '~async/transition-height/transition-height'
import IconMinus from '~icons/directional-navigation/arrow/new-minus.svg'
import IconPlus from '~icons/directional-navigation/plus.svg'
import NewPlus from '~icons/directional-navigation/arrow/new-plus.svg'

export default {
  name: 'AccordionSnippet',

  components: {
    IconMinus,
    IconPlus,
    TransitionHeight,
    NewPlus,
  },

  props: {
    accordionData: {
      type: Array,
      default: () => [],
    },
    blockClass: {
      type: String,
      default: '',
    },
    toggleHeadingTag: {
      type: String,
      default: 'h2',
    },
    useMinusIcon: {
      type: Boolean,
      default: false,
    },
    expandedIndex: {
      type: Number,
      default: null,
    },
  },

  data() {
    return {
      cnvsGlobal: true,
      accordions: [],
    }
  },

  watch: {

    /**
     * Watch for updates in accordionData.
     */
    accordionData() {
      this.setAccordions()
    },
  },

  mounted() {
    this.setAccordions()
  },

  methods: {

    /**
     * Set accordions array using props data.
     */
    setAccordions() {
      this.accordions = this.accordionData.map((accordion) => {
        return {
          active: false,
          ...accordion,
        }
      })
      if (this.expandedIndex !== null) {
        this.accordions[this.expandedIndex].active = true
      }
    },

    /**
     * Return formatted accordion title.
     * @param {Object} accordion - accordion object.
     * @return {String}
     */
    getAccordionTitle(accordion) {
      return accordion?.title?.replace('&amp;', '&')
    },

    /**
     * Check if an accordion is active using its index.
     * @param {Number} index - Index of a specific accordion.
     * @returns {Boolean}
     */
    isActive(index) {
      return this.accordions[index].active
    },

    /**
     * Toggles the clicked accordion
     * @param {String} accordionIndex - Index of the clicked accordion
     */
    toggleAccordion(accordionIndex) {
      if (this.isActive(accordionIndex)) {
        this.accordions[accordionIndex].active = false
        return
      }

      this.accordions.forEach((panel, index) => {
        panel.active = accordionIndex === index
      })
    },

    /**
     * Format answer body, so "A.P.C." always renders in caps.
     * @param {String} answer - Richtext from answer body setting
     */
    formatBody(answer) {
      if (!answer) {
        return ''
      }

      return answer.replace(/a[.]p[.]c[.]/gi, '<span class="site-name">A.P.C.</span>')
    },
  },
}
</script>
